/*************************************************************************
 *
 * c20g CONFIDENTIAL
 * __________________
 *
 *  [2007] - [2023] Counterpoint Consulting, Incorporated
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Counterpoint Consulting Incorporated.
 * The intellectual and technical concepts contained
 * herein are proprietary to Counterpoint Consulting Incorporated
 * and its suppliers and may be covered by U.S. and Foreign Patents,
 * patents in process, and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Counterpoint Consulting Incorporated.
 */

import _ from 'lodash';
import Backbone from 'backbone';

import StringCell from '@/backgrid/StringCell';
import GetAllRegions from '@/services/GetAllRegions';
import GetAllTrainingTypes from '@/services/GetAllTrainingTypes';
import GetAdditionalItemsByOrderFormTypeID from '@/services/GetAdditionalItemsByOrderFormTypeID';
import GetMFDAdditionalServicesByObjectID from '@/services/GetMFDAdditionalServicesByObjectID';
import DeliveryScheduleGridView from '@/views/DeliveryScheduleGridView';
import SpecialtyRequirementDeliveryScheduleGridView from '@/views/SpecialtyRequirementDeliveryScheduleGridView';
import A3DeliveryScheduleGridView from '@/views/A3DeliveryScheduleGridView';
import OrderTypes from '@/enums/OrderTypes';
import GetDeliveryScheduleColumnsByUserAndOrderType from '@/services/GetDeliveryScheduleColumnsByUserAndOrderType';

import GetCONUSAndOCONUSRegions from '../../order/services/GetCONUSAndOCONUSRegions';
import GetOrderPackageCustomerOrderDeliverySchedules from '../services/GetOrderPackageCustomerOrderDeliverySchedules';
import OrderPackageAccess from '../utils/OrderPackageAccess';

export default async function loadOrderPackageDeliveryScheduleGridView({ orderPackageID, editable, orderPackage }) {
  let additionalItemsService, data1, data2;
  if (orderPackage.orderFormTypeID === '1' || orderPackage.orderFormTypeID === '3') {
    //MFD
    additionalItemsService = GetMFDAdditionalServicesByObjectID;
    data1 = orderPackageID;
    data2 = 'ORDER PACKAGE';
  } else {
    additionalItemsService = GetAdditionalItemsByOrderFormTypeID;
    data1 = orderPackage.orderFormTypeID;
  }

  const [regions, schedules, trainingTypes, additionalItems, conusOconusRegions, prtnrA3Columns] = await Promise.all([
    GetAllRegions(),
    GetOrderPackageCustomerOrderDeliverySchedules(orderPackageID),
    GetAllTrainingTypes(),
    additionalItemsService(data1, data2),
    GetCONUSAndOCONUSRegions(),
    GetDeliveryScheduleColumnsByUserAndOrderType(orderPackage.orderFormTypeID)
  ]);

  const columns = prtnrA3Columns.filter(({ name }) => name !== 'lineClin')
    .map(({ name, label, width, renderableBy, downloadableBy }) => ({
      name,
      label,
      cell: StringCell,
      headerCell: 'custom',
      width: parseInt(width),
      editable: false,
      downloadable: OrderPackageAccess.isDeliveryScheduleColumnDownloadable(downloadableBy),
      renderable: OrderPackageAccess.isDeliveryScheduleColumnRenderable(renderableBy)
    }));

  //East, West, and OCONUS region group options should always be filtered out (DACS-9445)
  //regions with IsSpecialty = false should also be filtered out.
  const filteredRegions = regions?.filter((region) => {
    return (
      !(region.code === 'EAST' || region.code === 'WEST' || region.code === 'OCON') && region?.isSpecialty === 'false'
    );
  });

  const isReplacementOptions = [
    { id: 0, name: 'New' },
    { id: 1, name: 'Replacement' }
  ];

  const isMFD = orderPackage.orderFormTypeID === '1' || orderPackage.orderFormTypeID === '3' ? true : false;

  _.each(schedules, (schedule, i) => {
    let colorText, niprText;
    if (schedule.isColor === '1') {
      colorText = 'C';
    } else if (schedule.isColor === '0') {
      colorText = 'M';
    }
    if (schedule.isNipr === '1') {
      niprText = 'NIPR';
    } else if (schedule.isNipr === '0') {
      niprText = 'SIPR';
    }

    schedule.device = {
      model: schedule.model,
      laserPrinterTonerCartridgeType: schedule.laserPrinterTonerCartridgeType,
      configurationAndPricing: schedule.configurationAndPricing,
      description: schedule.description,
      volumeBand: schedule.volumeBand,
      colorText,
      niprText,
      optionalAccessories: schedule.optionalAccessories,
      equipmentName: schedule.equipmentName
    };

    schedule.isReplacementText =
      schedule.isReplacement === '0' ? 'New' : schedule.isReplacement === '1' ? 'Replacement' : '';
    schedule.trainingTypeText = schedule.trainingType;
    schedule.additionalServiceText = schedule.additionalService
      ? schedule.additionalService
      : schedule.mfdAdditionalService;
    schedule.additionalAccessoryText = schedule.additionalAccessory
      ? schedule.additionalAccessory
      : schedule.mfdAdditionalAccessory;
    schedule.colorText = colorText;
    schedule.niprText = niprText;
    schedule.formattedPrice = parseInt(schedule.price).toLocaleString('en-US', { style: 'currency', currency: 'USD' });
    setAddress(schedule);
    setEquipmentName(schedule, orderPackage.orderFormTypeID);
  });

  if (orderPackage.orderFormTypeID === OrderTypes.SPECIALTY_REQ) {
    // Ad Hoc
    return new SpecialtyRequirementDeliveryScheduleGridView({
      objectTypeCode: 'ORDER_PACKAGE',
      objectID: orderPackageID,
      itPOCExists: true,
      editable,
      isPriceVisible: true,
      model: new Backbone.Model({
        regions: filteredRegions,
        schedules,
        orderPackage,
        trainingTypes,
        isReplacementOptions,
        additionalServices: additionalItems.additionalServices,
        isMFD,
        conusOconusRegions
      })
    });
  } else if (orderPackage.orderFormTypeID === OrderTypes.PRTNR_A3) {
    return new A3DeliveryScheduleGridView({
      objectTypeCode: 'ORDER_PACKAGE',
      objectID: orderPackageID,
      itPOCExists: true,
      finPOCExists: true,
      editable,
      model: new Backbone.Model({
        regions: regions?.filter((region) => region?.isSpecialty === 'false'),
        columns,
        schedules,
        orderPackage,
        trainingTypes,
        isReplacementOptions,
        additionalServices: additionalItems.additionalServices,
        additionalAccessories: additionalItems.additionalAccessories,
        isMFD,
        conusOconusRegions
      })
    });
  } else {
    return new DeliveryScheduleGridView({
      objectTypeCode: 'ORDER_PACKAGE',
      objectID: orderPackageID,
      itPOCExists: true,
      editable,
      model: new Backbone.Model({
        regions: filteredRegions,
        schedules,
        orderPackage,
        trainingTypes,
        isReplacementOptions,
        additionalServices: additionalItems.additionalServices,
        additionalAccessories: additionalItems.additionalAccessories,
        isMFD,
        conusOconusRegions
      })
    });
  }
}

function setAddress(schedule) {
  schedule.addressLine1 = schedule.address.addressLine1;
  schedule.addressLine2 = schedule.address.addressLine2;
  schedule.streetAddress = schedule?.addressLine1?.concat(', ', schedule?.addressLine2);
  schedule.buildingFloorNumber = schedule.address.buildingFloorNumber;
  schedule.floorNumber = schedule.address.floorNumber;
  schedule.city = schedule.address.city;
  schedule.regionCode = schedule.address.regionCode;
  schedule.roomPoleNumber = schedule.address.roomPoleNumber;
  schedule.zip = schedule.address.zip;
  if (schedule.zip) {
    schedule.address.isConus = '1';
  }
}

function setEquipmentName(schedule, orderFormTypeID) {
  if (orderFormTypeID === OrderTypes.PRTNR_A3) {
    schedule.equipmentName = schedule.device.equipmentName;
    return;
  }

  if (schedule.device.model) {
    schedule.equipmentName = schedule.device.model;
  } else if (schedule.device.laserPrinterTonerCartridgeType) {
    schedule.equipmentName = schedule.device.laserPrinterTonerCartridgeType;
  } else if (schedule.device.configurationAndPricing) {
    schedule.equipmentName = schedule.device.volumeBand.concat('  -  ', schedule.device.configurationAndPricing);
  } else if (schedule.device.description) {
    schedule.equipmentName = schedule.device.description;
  } else if (schedule.device.volumeBand) {
    schedule.equipmentName = schedule.device.volumeBand.concat(schedule.device.niprText, schedule.device.colorText);
    if (schedule.device.optionalAccessories) {
      schedule.equipmentName = schedule.equipmentName.concat('  -  ', schedule.device.optionalAccessories);
    }
  } else {
    schedule.equipmentName = '';
  }
}
